import { supabase } from '../lib/supabase';

/**
 * Track API call metrics for monitoring and analytics
 * @param {object} metrics - Metrics data
 * @param {string} metrics.provider - Provider name ('openai' or 'perplexity')
 * @param {string} metrics.endpoint - API endpoint called
 * @param {number} metrics.responseTimeMs - Response time in milliseconds
 * @param {number} metrics.retryCount - Number of retry attempts
 * @param {boolean} metrics.success - Whether the call succeeded
 * @param {string} metrics.errorType - Type of error if failed
 * @param {string} metrics.errorMessage - Error message if failed
 * @param {number} metrics.statusCode - HTTP status code
 * @param {object} metrics.metadata - Additional metadata
 * @returns {Promise<void>}
 */
export async function trackApiCall(metrics) {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    
    const { error } = await supabase
      ?.from('api_call_metrics')
      ?.insert({
        user_id: user?.id || null,
        provider: metrics?.provider,
        endpoint: metrics?.endpoint,
        response_time_ms: metrics?.responseTimeMs,
        retry_count: metrics?.retryCount || 0,
        success: metrics?.success,
        error_type: metrics?.errorType || null,
        error_message: metrics?.errorMessage || null,
        status_code: metrics?.statusCode || null,
        metadata: metrics?.metadata || {}
      });

    if (error) {
      console.error('Failed to track API metrics:', error);
    }
  } catch (error) {
    // Silent fail - don't break the application if metrics tracking fails
    console.error('Error tracking API call:', error);
  }
}

/**
 * Get API call metrics with filtering
 * @param {object} filters - Filter options
 * @param {string} filters.provider - Filter by provider
 * @param {Date} filters.startDate - Start date for filtering
 * @param {Date} filters.endDate - End date for filtering
 * @param {number} filters.limit - Limit number of results
 * @returns {Promise<Array>}
 */
export async function getApiMetrics(filters = {}) {
  try {
    let query = supabase
      ?.from('api_call_metrics')
      ?.select('*')
      ?.order('created_at', { ascending: false });

    if (filters?.provider) {
      query = query?.eq('provider', filters?.provider);
    }

    if (filters?.startDate) {
      query = query?.gte('created_at', filters?.startDate?.toISOString());
    }

    if (filters?.endDate) {
      query = query?.lte('created_at', filters?.endDate?.toISOString());
    }

    if (filters?.limit) {
      query = query?.limit(filters?.limit);
    }

    const { data, error } = await query;

    if (error) throw error;

    return data || [];
  } catch (error) {
    console.error('Error fetching API metrics:', error);
    return [];
  }
}

/**
 * Get aggregated metrics for dashboard
 * @param {object} filters - Filter options
 * @returns {Promise<object>}
 */
export async function getAggregatedMetrics(filters = {}) {
  try {
    const metrics = await getApiMetrics(filters);

    const aggregated = {
      totalCalls: metrics?.length || 0,
      successfulCalls: metrics?.filter(m => m?.success)?.length || 0,
      failedCalls: metrics?.filter(m => !m?.success)?.length || 0,
      averageResponseTime: 0,
      averageRetryCount: 0,
      successRate: 0,
      byProvider: {},
      retryDistribution: {},
      errorTypes: {}
    };

    if (aggregated?.totalCalls > 0) {
      aggregated.averageResponseTime = Math.round(
        metrics?.reduce((sum, m) => sum + (m?.response_time_ms || 0), 0) / aggregated?.totalCalls
      );

      aggregated.averageRetryCount = (
        metrics?.reduce((sum, m) => sum + (m?.retry_count || 0), 0) / aggregated?.totalCalls
      )?.toFixed(2);

      aggregated.successRate = (
        (aggregated?.successfulCalls / aggregated?.totalCalls) * 100
      )?.toFixed(2);

      // Group by provider
      metrics?.forEach(m => {
        const provider = m?.provider;
        if (!aggregated?.byProvider?.[provider]) {
          aggregated.byProvider[provider] = {
            total: 0,
            successful: 0,
            failed: 0,
            avgResponseTime: 0,
            avgRetryCount: 0
          };
        }
        aggregated.byProvider[provider].total++;
        if (m?.success) {
          aggregated.byProvider[provider].successful++;
        } else {
          aggregated.byProvider[provider].failed++;
        }
      });

      // Calculate provider averages
      Object?.keys(aggregated?.byProvider)?.forEach(provider => {
        const providerMetrics = metrics?.filter(m => m?.provider === provider);
        const stats = aggregated?.byProvider?.[provider];
        
        stats.avgResponseTime = Math.round(
          providerMetrics?.reduce((sum, m) => sum + (m?.response_time_ms || 0), 0) / stats?.total
        );
        
        stats.avgRetryCount = (
          providerMetrics?.reduce((sum, m) => sum + (m?.retry_count || 0), 0) / stats?.total
        )?.toFixed(2);
        
        stats.successRate = ((stats?.successful / stats?.total) * 100)?.toFixed(2);
      });

      // Retry distribution
      metrics?.forEach(m => {
        const retries = m?.retry_count || 0;
        aggregated.retryDistribution[retries] = (aggregated?.retryDistribution?.[retries] || 0) + 1;
      });

      // Error types
      metrics?.filter(m => !m?.success && m?.error_type)?.forEach(m => {
        aggregated.errorTypes[m?.error_type] = (aggregated?.errorTypes?.[m?.error_type] || 0) + 1;
      });
    }

    return aggregated;
  } catch (error) {
    console.error('Error getting aggregated metrics:', error);
    return null;
  }
}

export default {
  trackApiCall,
  getApiMetrics,
  getAggregatedMetrics
};